import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Tab,
  Tabs,
  Typography,
  useTheme,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { useDispatch, useSelector } from "react-redux";
import { getAllFeedback } from "../../../core/repo/sessionRepo";
import Page from "../../../components/Page";
import {
  FolderOpenRounded,
  BuildRounded,
  ThumbDownAltRounded,
  ThumbUpAltRounded,
} from "@mui/icons-material";
import FeedbackDetail from "./ViewFeedbackDetail";
import { formatDate } from "./utils";
import Filters from "./Filters";
import { DataGrid } from "@mui/x-data-grid";
import Header from "./Header";
import { GradientIconLight } from "../../../components/GradientIcon";
import {
  CLICK_TAB_ON_FEEDBACK_PAGE,
  CLICK_FEEDBACK_TYPE_CHIP_ON_FEEDBACK_PAGE,
  APPLY_SORT_ON_FEEDBACK_PAGE,
  APPLY_SEARCH_ON_FEEDBACK_PAGE,
  OPEN_FEEDBACK_DIALOG_ON_FEEDBACK_PAGE,
  CLOSE_FEEDBACK_DIALOG_ON_FEEDBACK_PAGE,
} from "../../../utils/posthogEvents";
import { usePostHog } from "posthog-js/react";
import { v4 as uuidv4 } from "uuid";
import { useThemeSettings } from "../../../theme/ThemeSettingsProvider";

const useStyles = makeStyles()((theme) => ({
  root: {
    height: "100%",
  },
  feedbackCard: {
    marginBottom: theme.spacing(2),
    cursor: "pointer",
    transition: "transform 0.2s",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: theme.shadows[4],
    },
  },
  feedbackHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  feedbackContent: {
    marginTop: theme.spacing(2),
  },
  feedbackChip: {
    margin: theme.spacing(0.5),
  },
  highlightedSection: {
    backgroundColor: theme.palette.primary.lighter,
    padding: theme.spacing(1),
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  tabsContainer: {
    marginBottom: theme.spacing(3),
  },
  questionContent: {
    fontWeight: 500,
    marginBottom: theme.spacing(1),
  },
  answerContent: {
    color: theme.palette.text.secondary,
  },
  dateText: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
  },
  ratingIcon: {
    marginRight: theme.spacing(1),
  },
  gradientText: {
    background: theme.palette.accentGradient,
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  filterChip: {
    color: theme.palette.text.secondary,
  },
  activeFilterChip: {
    fontWeight: 500,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.selection,
  },
  positiveChip: {
    backgroundColor: "#E8F5E9",
    color: "#1B5E20",
    "& .MuiChip-icon": {
      color: "#1B5E20",
      fontSize: 18,
    },
  },
  partiallyIncorrectChip: {
    backgroundColor: "#FFF3E0",
    color: "#E65100",
    "& .MuiChip-icon": {
      color: "#E65100",
      fontSize: 18,
    },
  },
  completelyIncorrectChip: {
    backgroundColor: "#FFEBEE",
    color: "#D32F2F",
    "& .MuiChip-icon": {
      color: "#D32F2F",
      fontSize: 18,
    },
  },
  indoramaMonochromeFeedbackChip: {
    color: "#CFE7E8",
    background: "rgba(35,224,210,0.08)",
    fontWeight: 500,
    "& .MuiChip-icon": {
      color: "#6CF2E6",
      fontSize: 18,
    },
  },
  chip: {
    backgroundColor: "white",
  },
  aiModelFixChip: {
    backgroundColor: "#F9FBE7",
    color: "#827717",
    "& .MuiChip-icon": {
      color: "#827717",
      fontSize: 18,
    },
  },
  docUpdateChip: {
    backgroundColor: "#E3F2FD",
    color: "#0D47A1",
    "& .MuiChip-icon": {
      color: "#0D47A1 !important",
      fontSize: 18,
    },
  },
  approvedStatusChip: {
    backgroundColor: "#E8F5E9", // greenish
    color: "#1B5E20",
    fontWeight: 500,
  },
  rejectedStatusChip: {
    backgroundColor: "#FFEBEE", // reddish
    color: "#D32F2F",
    fontWeight: 500,
  },
  notifiedStatusChip: {
    backgroundColor: "#FFFDE7", // yellowish
    color: "#F9A825",
    fontWeight: 500,
  },
  raisedStatusChip: {
    backgroundColor: "#E3F2FD", // bluish
    color: "#1565C0",
    fontWeight: 500,
  },
  indoramaMonochromeStatusChip: {
    color: "#CFE7E8",
    background: "rgba(35,224,210,0.08)",
    fontWeight: 500,
    "& .MuiChip-icon": {
      color: "#6CF2E6",
    },
  },
}));

const feedbackTabs = [
  {
    label: "Feedback on your Documents",
    value: 0,
    icon: FolderOpenRounded,
  },
  {
    label: "Feedback for Raven",
    value: 1,
    icon: BuildRounded,
  },
];

const useFeedbackTabStyles = makeStyles()((theme) => ({
  gradientText: {
    background: theme.palette.light,
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  customTabs: {
    "& .MuiTabs-indicator": {
      display: "block",
      height: 4,
      borderRadius: "50px 50px 0px 0px",
      background: theme.palette.light,
    },
  },
}));

export const filterOptions = [
  { label: "All", value: "all" },
  { label: "Positive", value: "positive" },
  { label: "Negative", value: "negative" },
];

export const sortOptions = [
  { label: "Date", value: "date" },
  { label: "Status", value: "status" },
];

export const actionLabels = {
  ai_model_fix: {
    label: "AI Model Fix",
    icon: <BuildRounded sx={{ fontSize: 18 }} />,
  },
  doc_update: {
    label: "Doc Update",
    icon: <FolderOpenRounded sx={{ fontSize: 18 }} />,
  },
};

function ViewFeedback() {
  const { mode } = useThemeSettings();
  const isDarkMode = mode === "dark";
  const posthog = usePostHog();
  const { classes } = useStyles();
  const { classes: tabClasses } = useFeedbackTabStyles();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [error, setError] = useState(null);
  const { feedbackReports, isFeedbackLoading } = useSelector(
    (state) => state.session
  );
  const [filteredReports, setFilteredReports] = useState([]);
  const [tabValue, setTabValue] = useState(filterOptions[0].value);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState(sortOptions[0].value); // default sort is 'date'
  // New: tab for feedback type
  const [feedbackTab, setFeedbackTab] = useState(0); // 0: Docs, 1: Raven
  const { selectedDomain } = useSelector((state) => state.domain);

  // New: filter feedback based on feedbackTab
  useEffect(() => {
    let baseReports = [];
    if (feedbackTab === 0) {
      // Feedback on docs: action !== 'ai_model_fix'
      baseReports = feedbackReports.filter((r) => r.action !== "ai_model_fix");
    } else {
      // Feedback for Raven: action === 'ai_model_fix'
      baseReports = feedbackReports.filter((r) => r.action === "ai_model_fix");
    }
    // Apply tabValue filter (positive/negative/all)
    let filtered = baseReports;
    switch (tabValue) {
      case "positive":
        filtered = filtered.filter(
          (report) => report?.rating === "positive" || report?.rating === "good"
        );
        break;
      case "negative":
        filtered = filtered.filter(
          (report) => report?.rating === "negative" || report?.rating === "bad"
        );
        break;
      default:
        break;
    }
    // Apply query filter
    if (query) {
      filtered = filtered.filter(
        (report) =>
          report.question_content.toLowerCase().includes(query.toLowerCase()) ||
          report.feedback.toLowerCase().includes(query.toLowerCase()) ||
          report.description.toLowerCase().includes(query.toLowerCase())
      );
    }
    // Apply sort
    if (sort === "date") {
      filtered = [...filtered].sort((a, b) => {
        const dateA = new Date(a.updated_at || 0);
        const dateB = new Date(b.updated_at || 0);
        return dateB - dateA;
      });
    } else if (sort === "status") {
      filtered = [...filtered].sort((a, b) => {
        // Sort by approval_status alphabetically, fallback to empty string
        return (a.approval_status || "").localeCompare(b.approval_status || "");
      });
    }
    setFilteredReports(filtered);
  }, [feedbackReports, feedbackTab, tabValue, query, sort]);

  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        setLoading(true);
        await dispatch(getAllFeedback(selectedDomain["id"]));
        setError(null);
      } catch (err) {
        console.error("Failed to fetch feedback:", err);
        setError("Failed to load feedback. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchFeedback();
  }, [dispatch, selectedDomain]);

  const handleTabChange = (tab) => {
    setTabValue(tab);
    switch (tab) {
      case "positive":
        setFilteredReports(
          feedbackReports.filter(
            (report) =>
              report?.rating === "positive" || report?.rating === "good"
          )
        );
        break;
      case "negative":
        setFilteredReports(
          feedbackReports.filter(
            (report) =>
              report?.rating === "negative" || report?.rating === "bad"
          )
        );
        break;
      default:
        setFilteredReports(feedbackReports);
        break;
    }
    posthog.capture(CLICK_FEEDBACK_TYPE_CHIP_ON_FEEDBACK_PAGE, {
      tab: tab,
    });
  };

  const handleQueryChange = (event) => {
    setQuery(event.target.value);
    setFilteredReports(
      feedbackReports.filter(
        (report) =>
          report.question_content
            .toLowerCase()
            .includes(event.target.value.toLowerCase()) ||
          report.feedback
            .toLowerCase()
            .includes(event.target.value.toLowerCase()) ||
          report.description
            .toLowerCase()
            .includes(event.target.value.toLowerCase())
      )
    );
    posthog.capture(APPLY_SEARCH_ON_FEEDBACK_PAGE, {
      query: event.target.value,
    });
  };

  const handleSortChange = (event) => {
    const sortValue = event.target.value;
    setSort(sortValue);

    switch (sortValue) {
      case "date":
        setFilteredReports(
          [...filteredReports].sort((a, b) => {
            const dateA = new Date(a.updated_at || 0);
            const dateB = new Date(b.updated_at || 0);
            return dateB - dateA;
          })
        );
        break;
      case "status":
        setFilteredReports(
          [...filteredReports].sort((a, b) => {
            // Sort by approval_status alphabetically, fallback to empty string
            return (a.approval_status || "").localeCompare(
              b.approval_status || ""
            );
          })
        );
        break;
      default:
        setFilteredReports(feedbackReports);
        break;
    }
    posthog.capture(APPLY_SORT_ON_FEEDBACK_PAGE, {
      sort: sortValue,
    });
  };

  const handleFeedbackClick = (feedback) => {
    setSelectedFeedback(feedback);
    setDetailDialogOpen(true);
    posthog.capture(OPEN_FEEDBACK_DIALOG_ON_FEEDBACK_PAGE, {
      feedback: feedback,
    });
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    posthog.capture(CLOSE_FEEDBACK_DIALOG_ON_FEEDBACK_PAGE);
  };

  const handleFeedbackTabChange = (tab) => {
    posthog.capture(CLICK_TAB_ON_FEEDBACK_PAGE, {
      tab: feedbackTabs[tab].label,
    });
    setFeedbackTab(tab);
  };

  const columns = [
    {
      field: "id",
      headerName: "FeedbackID",
      width: 120,
      valueGetter: (id) => {
        return `ID#${id}`;
      },
    },
    { field: "question_content", headerName: "Query", flex: 3 },
    {
      field: "feedback",
      headerName: "Feedback",
      width: 400,
      renderCell: (params) => {
        const chips = ["positive", "good"].includes(params.row?.rating)
          ? ["positive"]
          : [
              params.row?.partial
                ? "partially_incorrect"
                : "completely_incorrect",
              ...params.row?.feedback.split(","),
            ];
        return (
          <Box height={"100%"} display="flex" alignItems="center">
            <Box
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              gap={1}
              p={0.5}
              sx={{
                background: (theme) => theme.palette.background.dark,
                borderRadius: "50px",
              }}>
              {chips.slice(0, 2).map((chip) => {
                // Only icon, no label, for thumbs up/down chips
                if (chip === "positive") {
                  return (
                    <Chip
                      icon={<ThumbUpAltRounded />}
                      key={chip}
                      className={
                        isDarkMode
                          ? classes.indoramaMonochromeFeedbackChip
                          : classes.positiveChip
                      }
                      label=""
                    />
                  );
                } else if (
                  chip === "partially_incorrect" ||
                  chip === "completely_incorrect"
                ) {
                  return (
                    <Chip
                      icon={<ThumbDownAltRounded />}
                      key={chip}
                      className={
                        chip === "partially_incorrect"
                          ? isDarkMode
                            ? classes.indoramaMonochromeFeedbackChip
                            : classes.partiallyIncorrectChip
                          : isDarkMode
                          ? classes.indoramaMonochromeFeedbackChip
                          : classes.completelyIncorrectChip
                      }
                      label=""
                    />
                  );
                } else {
                  return (
                    <Chip
                      key={chip}
                      label={chip
                        .split("_")
                        .map(
                          (word) => word.charAt(0).toUpperCase() + word.slice(1)
                        )
                        .join(" ")}
                      className={
                        isDarkMode
                          ? classes.indoramaMonochromeFeedbackChip
                          : classes.chip
                      }
                    />
                  );
                }
              })}
              {chips.length > 2 && (
                <Chip
                  label={`+${chips.length - 2}`}
                  className={
                    isDarkMode
                      ? classes.indoramaMonochromeFeedbackChip
                      : classes.chip
                  }
                />
              )}
            </Box>
          </Box>
        );
      },
    },
    { field: "description", headerName: "Comment", flex: 1.5 },
    {
      field: "created_at",
      headerName: "Date",
      width: 140,
      renderCell: (params) => formatDate(params.row.created_at),
      sortComparator: (v1, v2, cellParams1, cellParams2) => {
        // Sort by date descending (newest first)
        const date1 = cellParams1?.row?.created_at || 0;
        const date2 = cellParams2?.row?.created_at || 0;
        return new Date(date2) - new Date(date1);
      },
    },
    // {
    //   field: "action",
    //   headerName: "Action Required",
    //   width: 150,
    //   renderCell: (params) => {
    //     const action = params.row.action || "ai_model_fix";
    //     return (
    //       <Chip
    //         icon={actionLabels[action].icon}
    //         label={actionLabels[action].label}
    //         className={
    //           action === "ai_model_fix"
    //             ? classes.aiModelFixChip
    //             : classes.docUpdateChip
    //         }
    //       />
    //     );
    //   },
    // },
    // {
    //   field: "raised_by",
    //   headerName: "Raised by",
    //   width: 150,
    //   hide: true,
    //   renderCell: (params) => {
    //     return (
    //       <Box
    //         height={"100%"}
    //         display="flex"
    //         flexDirection="column"
    //         justifyContent="center">
    //         <Typography variant="body2">{params.row.user?.name}</Typography>
    //         <Typography fontSize={12} color="text.secondary">
    //           {formatDate(params.row.created_at)}
    //         </Typography>
    //       </Box>
    //     );
    //   },
    // },
    {
      field: "approval_status",
      headerName: "Status",
      width: 150,
      renderCell: (params) => {
        const status = params.row.approval_status;
        let label = "Feedback Raised";
        let chipClass = classes.raisedStatusChip;
        if (status === "approved") {
          label = "Approved";
          chipClass = classes.approvedStatusChip;
        } else if (status === "rejected") {
          label = "Rejected";
          chipClass = classes.rejectedStatusChip;
        } else if (status === "notified") {
          label = "Notified";
          chipClass = classes.notifiedStatusChip;
        }
        // In dark mode, always use indorama-monochrome class
        if (isDarkMode) {
          chipClass = classes.indoramaMonochromeStatusChip;
        }
        return <Chip label={label} className={chipClass} size="small" />;
      },
    },
  ];

  return (
    <Page title="Answer Feedback" className={classes.root}>
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Header />
        {/* Styled Feedback Tabs */}
        <Box>
          <Tabs
            centered
            value={feedbackTab}
            onChange={(_, v) => handleFeedbackTabChange(v)}
            className={tabClasses.customTabs}
            aria-label="feedback tabs">
            {feedbackTabs.map((tab) => {
              const isSelected = tab.value === feedbackTab;
              return (
                <Tab
                  key={tab.value}
                  sx={{ minHeight: 52 }}
                  label={
                    <Box display={"flex"} alignItems={"center"}>
                      <span
                        className={isSelected ? tabClasses.gradientText : ""}>
                        {tab.label}
                      </span>
                    </Box>
                  }
                  value={tab.value}
                  icon={
                    isSelected ? (
                      isDarkMode ? (
                        <tab.icon />
                      ) : (
                        <GradientIconLight
                          Icon={tab.icon}
                          sx={{ marginRight: 1 }}
                        />
                      )
                    ) : (
                      <tab.icon />
                    )
                  }
                  iconPosition="start"
                />
              );
            })}
          </Tabs>
          <Divider />
        </Box>
        <Filters
          tab={tabValue}
          onTabChange={handleTabChange}
          query={query}
          onQueryChange={handleQueryChange}
          sort={sort}
          onSortChange={handleSortChange}
        />
        <Box flex={1} overflow={"auto"} p={3} pt={0}>
          <DataGrid
            rows={filteredReports}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 14,
                },
              },
              sorting: {
                sortModel: [{ field: "created_at", sort: "desc" }],
              },
            }}
            pageSizeOptions={[14]}
            loading={loading}
            disableRowSelectionOnClick={true}
            rowSelection={false}
            disableColumnFilter
            disableColumnMenu
            disableColumnSorting
            onRowClick={(params) => handleFeedbackClick(params.row)}
            sx={{
              borderRadius: 3,
              "& .MuiDataGrid-cell": {
                cursor: "pointer",
              },
              "& .MuiDataGrid-cell:focus": {
                outline: "none",
              },
              "& .MuiDataGrid-cell:focus-within": {
                outline: "none",
              },
              "& .MuiDataGrid-columnSeparator": {
                display: "none",
              },
              "& .MuiDataGrid-columnHeader:focus": {
                outline: "none",
              },
              "& .MuiDataGrid-columnHeader:focus-within": {
                outline: "none",
              },
              "& .MuiDataGrid-columnHeader": {
                cursor: "default",
                backgroundColor: (theme) => theme.palette.background.light,
              },
            }}
          />
        </Box>
        {detailDialogOpen && (
          <FeedbackDetail
            feedback={selectedFeedback}
            onClose={handleCloseDetailDialog}
          />
        )}
      </Box>
    </Page>
  );
}

export default ViewFeedback;
